import { UsersResultsShare } from '@components/share';
import { useSnackbar } from '@components/snackbar';
import {
  BoardIcon,
  CloseIcon,
  DocumentIcon,
  EmbedViewIcon,
  WhiteBoardIcon,
} from '@sketchnote/icons';
import { LoaderXs } from '@sketchnote/shared-components';
import { useRouter } from 'next/router';
import * as React from 'react';
import { API } from 'src/api';
import { useAuth } from 'src/shared/hooks/use-auth';
import useSWR from 'swr';

import { useFileService } from '../api/useFileServices';
import { DropdownCheckbox } from '../dropdown-checkbox';
import { useFileAccess } from '../hooks/useFileAccess';
import { useFileStore } from '../store/useFileStore';
const { useCallback, useEffect, useState } = React;

interface FileItem {
  label: string;
  value: string;
  view: string;
}

export const ManageViews = ({ onClose }: { onClose: () => void }) => {
  const router = useRouter();
  const [views, setViews] = useState({});
  const [access, setAccess] = useState({});
  const [isSaving, setSaving] = useState(false);
  const [sharedUsers, setSharedUsers] = useState<any[]>([]);
  const [selection, setSelection] = useState<any>({});
  const [hasChanged, setChanged] = useState<boolean>(false);
  const [isAdmin, setAdmin] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [openSnackbar] = useSnackbar();
  const [modifiedUsers, setModifiedUsers] = useState<string[]>([]);

  const { isFullAccess } = useFileAccess();

  const { fetchManageViewData } = useFileService();
  const userDetails = useAuth((s) => s.userDetails);
  const currentFile = useFileStore(
    useCallback((s) => s.files[router.query.slug as string], [router.query.slug as string])
  );

  const internalFiles =
    currentFile?.internalFiles?.map((file) => ({
      view: file.type === 'board' ? 'task' : file.type,
      value: file.id,
      label: file.name ? file.name : file.type === 'board' ? 'task' : file.type,
    })) || [];

  const fileItems = [
    {
      label: currentFile?.primaryFile?.name ? currentFile?.primaryFile?.name : 'Untitled',
      value: currentFile?.primaryFile?.id,
      view: currentFile?.primaryFile?.type === 'board' ? 'task' : currentFile?.primaryFile?.type,
    },
    ...internalFiles,
  ];

  const fileOptions = fileItems.reduce(
    (acc, item: FileItem) => {
      if (item.view === 'task') {
        acc.board.push(item);
      } else {
        acc.other.push(item);
      }
      return acc;
    },
    { board: [] as FileItem[], other: [] as FileItem[] }
  );

  const fileItemOptions: FileItem[] = [...fileOptions.other];
  let totalFiles = fileItemOptions.length;
  if (fileOptions.board.length) {
    fileItemOptions.push({
      label: 'Task views',
      value: fileOptions.board[0].value,
      view: 'task',
    });
    totalFiles += 1;
  }

  useEffect(() => {
    setLoading(true);
    const fetchManageView = async () => {
      const fileId = router?.query?.slug as string;
      if (fileId) {
        const response: any = await fetchManageViewData(fileId);
        if (response?.data?.success) setLoading(false);
        const responseData = response?.data?.data || [];
        const memberInformation = userDetails?.memberInformation;

        const { users, hasAccess } = responseData.reduce(
          (acc, { member }) => {
            if (member?.memberId !== memberInformation?.memberId) {
              acc.users.push({ member });
            } else if (member?.role === 'admin') {
              acc.hasAccess = true;
            }
            return acc;
          },
          { users: [], hasAccess: false }
        );

        setAdmin(hasAccess);
        setSharedUsers(users);
        const transformedAccess = {};
        const newSelection = {};
        const initialFiles = {};
        fileItemOptions?.forEach((option) => {
          initialFiles[option.value] = true;
        });
        users.forEach((user) => {
          const { member } = user || {};
          const memberId = member?._id;
          transformedAccess[memberId] = { ...initialFiles };
          newSelection[memberId] = false;
        });
        setAccess(transformedAccess);
        setSelection(newSelection);
        const result = response?.data?.data.reduce((acc, { member, views }) => {
          acc[member._id] = Array.isArray(views)
            ? views.map((view) => ({ value: view?.file }))
            : fileItemOptions;
          return acc;
        }, {});
        setViews(result);
      }
    };
    fetchManageView();
  }, []);

  const handleFileAccess = () => {
    if (!hasChanged) {
      onClose();
      return;
    }
    setSaving(true);

    const modifiedAccesses = modifiedUsers.reduce(
      (a, c) => Object.assign(a, { [c]: access[c] }),
      {}
    );

    const payload = {
      file: currentFile?.id,
      access: modifiedAccesses,
    };

    API.post('/files/access', payload)
      .then((res) => {
        openSnackbar({
          text: 'access successfully updated',
          snackType: 'standard',
          position: 'bottom-center',
        });
        onClose();
      })
      .catch((e) => {
        openSnackbar({
          text: 'Error',
          snackType: 'error',
          position: 'bottom-center',
        });
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const handleMenuItems = (memberId: string, items: FileItem[]) => {
    if (!hasChanged) setChanged(true);
    const selectedViews = items.map((item) => item.value);
    const updatedAccess = { ...access };
    if (updatedAccess[memberId]) {
      for (const viewId in updatedAccess[memberId]) {
        updatedAccess[memberId][viewId] = selectedViews.includes(viewId);
      }
    }

    setSelection({
      ...selection,
      [memberId]: Object.values(updatedAccess[memberId]).every((v) => v === false),
    });

    setModifiedUsers((prev) => [...prev, memberId]);
    setAccess(updatedAccess);
  };

  return (
    <>
      <div>
        <div className="flex justify-between mb-8 w-full">
          <span className="text-gray-999 text-lg">Manage view access</span>
          <button aria-label="close button" onClick={onClose}>
            <CloseIcon className="text-gray-600 text-xs" />
          </button>
        </div>
        <span className=" text-gray-600 text-sm">
          Add or remove view access for individual views
        </span>
        {isLoading ? (
          <div className="flex justify-center items-center h-100 w-full">
            <LoaderXs />
          </div>
        ) : (
          <>
            {isFullAccess ? (
              <div className="max-h-256 overflow-y-scroll mt-20 mb-32 no-scrollbar">
                {sharedUsers?.map((member) => (
                  <div key={member?.member._id} className="flex justify-between">
                    <UsersResultsShare
                      className="members -ml-12"
                      email={member?.member?.email}
                      username={member?.member?.firstname + ' ' + member?.member?.lastname}
                      id={member?.member?.member?._id}
                      avatar={member?.member?.avatar}
                      // role={member?.role}
                    />

                    <DropdownCheckbox
                      menuItems={fileItemOptions}
                      usePortal
                      onChangeMenuItem={handleMenuItems.bind(null, member?.member?._id)}
                      selectedItems={views[member?.member?._id] || []}
                      totalItems={totalFiles}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-gray-999 mt-6 text-sm ">
                Sorry, you do not have permission to set view access.
              </div>
            )}
          </>
        )}

        {isLoading || (
          <div className="flex justify-end mt-12 w-full">
            <button
              onClick={onClose}
              className="border-blue-500 text-blue-500 border rounded-sm px-24 py-8 mr-8"
            >
              Cancel
            </button>
            {isFullAccess && (
              <button
                className={` text-white border rounded-sm px-24 py-8 ${
                  isSaving || Object.values(selection).some((v) => v === true)
                    ? 'bg-blue-200'
                    : 'bg-blue-500'
                }`}
                onClick={handleFileAccess}
                disabled={isSaving || Object.values(selection).some((v) => v === true)}
              >
                Save
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
};
